import React from 'react';
import styled from 'styled-components';

const OptionsContainer = styled.div`
  margin: 20px 0;
  padding: 15px;
  background-color: #f5f5f5;
  border-radius: 4px;

  @media (max-width: 767px) {
    margin: 15px 0;
    padding: 12px;
  }
`;

const OptionSetWrapper = styled.div`
  margin-bottom: 15px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background-color: white;

  &:focus {
    outline: none;
    border-color: #4caf50;
  }

  @media (max-width: 767px) {
    padding: 10px 12px;
    font-size: 16px; /* Prevents zoom on iOS */
  }
`;

const SelectedOption = styled.div`
  margin-top: 8px;
  padding: 8px 10px;
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  font-size: 14px;
  color: #666;
`;

const SelectedLabel = styled.span`
  font-weight: 500;
  color: #333;
  margin-right: 4px;
`;

const DescriptionText = styled.p`
  a {
    color: #0066cc;
    text-decoration: underline;
  }
`;

const getOptionValue = (option) => option.key || String(option.id);

const optionValueMatches = (option, selectedOption) => {
  if (!option || !selectedOption) return false;
  return (
    option.key === selectedOption ||
    String(option.id) === String(selectedOption)
  );
};

const sortByPosition = (first, second) =>
  (first.position || 0) - (second.position || 0);

export const getProductOptionSets = (product) => {
  const optionSets = product?.product_option_sets || [];
  const hasNewStructure = optionSets.length > 0;

  if (hasNewStructure) return optionSets;

  if (product?.product_options?.length > 0) {
    return [
      {
        id: 'legacy',
        key: 'legacy',
        name: 'Product Options',
        display_name: 'Product Option',
        product_options: product.product_options,
      },
    ];
  }

  return [];
};

export const getDefaultProductOptionValue = (optionSet) => {
  if (!optionSet?.product_options?.length) return null;

  const defaultOption = optionSet.product_options.find((opt) => opt.is_default);
  const fallbackOption = [...optionSet.product_options].sort(sortByPosition)[0];
  const option =
    defaultOption || (optionSet.selection_required && fallbackOption);

  return option ? getOptionValue(option) : null;
};

export const getDefaultProductOptions = (product) => {
  const defaults = {};

  getProductOptionSets(product).forEach((optionSet) => {
    const defaultOptionValue = getDefaultProductOptionValue(optionSet);
    if (defaultOptionValue) {
      defaults[optionSet.key] = defaultOptionValue;
    }
  });

  return defaults;
};

export const getProductOptionsDefaultsKey = (product) => {
  if (!product) return null;

  const productIdentity = product.id || product.key || 'unknown-product';
  const optionSetSignature = getProductOptionSets(product).map((optionSet) => ({
    id: optionSet.id,
    key: optionSet.key,
    selection_required: optionSet.selection_required === true,
    product_options: (optionSet.product_options || [])
      .map((option) => ({
        id: option.id,
        key: option.key,
        position: option.position || 0,
        is_default: option.is_default === true,
      }))
      .sort((first, second) => {
        const firstPosition = first.position || 0;
        const secondPosition = second.position || 0;
        if (firstPosition !== secondPosition) {
          return firstPosition - secondPosition;
        }
        return String(first.key || first.id).localeCompare(
          String(second.key || second.id),
        );
      }),
  }));

  return JSON.stringify({
    productIdentity,
    optionSetSignature,
  });
};

export const reconcileProductOptions = (product, selectedOptions = {}) => {
  const nextOptions = {};

  getProductOptionSets(product).forEach((optionSet) => {
    const optionSetKey = optionSet.key;
    const selectedOption = selectedOptions?.[optionSetKey];
    const hasSelectedValue =
      selectedOption !== undefined && selectedOption !== '';
    const selectedOptionStillValid =
      hasSelectedValue &&
      optionSet.product_options?.some((option) =>
        optionValueMatches(option, selectedOption),
      );

    if (selectedOptionStillValid) {
      nextOptions[optionSetKey] = selectedOption;
      return;
    }

    if (optionSet.selection_required) {
      const defaultOptionValue = getDefaultProductOptionValue(optionSet);
      if (defaultOptionValue) {
        nextOptions[optionSetKey] = defaultOptionValue;
      }
      return;
    }

    if (Object.prototype.hasOwnProperty.call(selectedOptions, optionSetKey)) {
      nextOptions[optionSetKey] = '';
    }
  });

  return nextOptions;
};

const ProductOptionsSelector = ({
  product,
  selectedOptions,
  onOptionChange,
  allowDeselection = true, // Allow users to deselect even pre-selected options
}) => {
  const allOptionSets = getProductOptionSets(product);

  if (allOptionSets.length === 0) {
    return null;
  }

  return (
    <OptionsContainer>
      {allOptionSets.map((optionSet) => {
        const selectedOption = selectedOptions?.[optionSet.key];
        const selectedOptionData = optionSet.product_options?.find((opt) =>
          optionValueMatches(opt, selectedOption),
        );

        return (
          <OptionSetWrapper key={optionSet.id}>
            <Label htmlFor={`option-set-${optionSet.id}`}>
              {optionSet.display_name || optionSet.name || 'Product Option'}
            </Label>
            {optionSet.description && (
              <DescriptionText
                dangerouslySetInnerHTML={{ __html: optionSet.description }}
              />
            )}
            <Select
              id={`option-set-${optionSet.id}`}
              name={`option-set-${optionSet.id}`}
              value={selectedOption || ''}
              onChange={(e) => onOptionChange(optionSet.key, e.target.value)}
            >
              {allowDeselection && !optionSet.selection_required && (
                <option value="">None</option>
              )}
              {optionSet.product_options?.map((option) => {
                const priceDisplay = option.price
                  ? ` (+$${(option.price / 100).toFixed(2)})`
                  : '';
                return (
                  <option key={option.id} value={getOptionValue(option)}>
                    {option.name}
                    {priceDisplay}
                    {option.is_default ? ' (default)' : ''}
                  </option>
                );
              })}
            </Select>

            {selectedOptionData && (
              <SelectedOption>
                <SelectedLabel>Selected:</SelectedLabel>
                {selectedOptionData.name}
                {selectedOptionData.price > 0 && (
                  <span style={{ marginLeft: '8px' }}>
                    (+${(selectedOptionData.price / 100).toFixed(2)})
                  </span>
                )}
              </SelectedOption>
            )}
          </OptionSetWrapper>
        );
      })}
    </OptionsContainer>
  );
};

export default ProductOptionsSelector;
